import React from 'react'

import { Heading4, Heading3, Box, Display, Space, Text } from '@gousto-internal/citrus-react'
import Immutable from 'immutable'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'

import { SocialButton } from 'components/SocialLinks/SocialButton'
import { SocialShareButtons } from 'components/SocialLinks/SocialShareButtons'
import { SocialShareSheetCTA } from 'components/SocialLinks/SocialShareSheetCTA'
import { UserRAFLink } from 'components/UserRAFLink'
import { getDiscountDurationFormatted } from 'selectors/rafOffer'

import { Offer } from './Offer'

import css from './ReferAFriend.css'

const propTypes = {
  rafOffer: ImmutablePropTypes.map({
    creditFormatted: PropTypes.string,
    firstBoxDiscountFormatted: PropTypes.string,
    firstMonthDiscountFormatted: PropTypes.string,
  }),
  referralCode: PropTypes.string.isRequired,
  userFirstName: PropTypes.string.isRequired,
  device: PropTypes.string.isRequired,
  trackingReferFriend: PropTypes.func.isRequired,
  trackingReferFriendSocialSharing: PropTypes.func.isRequired,
  trackUserFreeFoodLinkShare: PropTypes.func.isRequired,
  isSignUpSeamlessFlowIntoPlanningEnabled: PropTypes.bool,
}

const defaultProps = {
  rafOffer: Immutable.Map(),
  isSignUpSeamlessFlowIntoPlanningEnabled: false,
}

const ReferAFriend = ({
  rafOffer,
  referralCode,
  userFirstName,
  device,
  trackingReferFriend,
  trackingReferFriendSocialSharing,
  trackUserFreeFoodLinkShare,
  isSignUpSeamlessFlowIntoPlanningEnabled,
}) => {
  const [isFacebookInitiated, setIsFacebookInitiated] = React.useState(false)
  const secondBoxDiscountFormatted = rafOffer.get('secondBoxDiscountFormatted')
  const yourOffer = rafOffer.get('creditFormatted')
  const theirBoxOffer = rafOffer.get('firstBoxDiscountFormatted')
  const theirMonthOffer = rafOffer.get('firstMonthDiscountFormatted')
  const subtitle = rafOffer.get('subtitle')
  const discountDuration = getDiscountDurationFormatted(rafOffer)
  const previousCreditOffer = rafOffer.getIn(['reducedCampaignDetails', 'creditFormatted'])

  return (
    <section data-testing="referAFriendSection">
      <Box>
        <Box display={['block', 'block', 'none', 'none']}>
          <Heading4 data-testing="referAFriendSectionTitle" size={5}>
            Invite your friends to Gousto
          </Heading4>
        </Box>
        <Box display={['none', 'none', 'block', 'block']}>
          <Heading3 size={7}>Invite your friends to Gousto</Heading3>
        </Box>
        {subtitle && (
          <Box marginTop={2} marginBottom={4}>
            <Text size={[2, 2, 4, 4]}>{subtitle}</Text>
          </Box>
        )}
        <Space size={4} />
        <Box display={Display.Flex} gap={4}>
          <Offer
            isYourOffer
            offer={yourOffer}
            secondBoxDiscount={secondBoxDiscountFormatted}
            previousOffer={previousCreditOffer}
          />
          <Box
            aria-orientation="vertical"
            role="separator"
            width="0px"
            alignSelf="stretch"
            style={{
              borderLeft: '1px dashed var(--colors-border, #D0D5DD)',
              margin: '0 16px',
            }}
          />
          <Offer
            isYourOffer={false}
            offer={theirBoxOffer}
            theirMonthOffer={theirMonthOffer}
            discountDuration={discountDuration}
            secondBoxDiscount={secondBoxDiscountFormatted}
          />
        </Box>
        <Space size={4} />
        {!isSignUpSeamlessFlowIntoPlanningEnabled && (
          <Box display={['none', 'none', 'block', 'block']}>
            <UserRAFLink
              classLinkContainer={css.rafLink}
              referralCode={referralCode}
              trackingReferFriend={trackingReferFriend}
              trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
              isModal
            >
              <SocialButton text="Copy Invite Link" type="link" elementType="component" />
            </UserRAFLink>
            {isFacebookInitiated && (
              <p className={css.socialButtonsText}>Share your invite code:</p>
            )}
            <SocialShareButtons
              referralCode={referralCode}
              userFirstName={userFirstName}
              device={device}
              trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
              trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
              elementType="component"
              onFacebookInitiated={() => setIsFacebookInitiated(true)}
            />
          </Box>
        )}
        {isSignUpSeamlessFlowIntoPlanningEnabled ? (
          <SocialShareSheetCTA
            referralCode={referralCode}
            trackingReferFriend={trackingReferFriend}
            isFixed={false}
            marginSize={4}
          />
        ) : (
          <Box
            data-testing="socialShareSheetCTAHidden"
            display={['block', 'block', 'none', 'none']}
          >
            <SocialShareSheetCTA
              referralCode={referralCode}
              trackingReferFriend={trackingReferFriend}
              isFixed={false}
            />
          </Box>
        )}
      </Box>
    </section>
  )
}

ReferAFriend.propTypes = propTypes

ReferAFriend.defaultProps = defaultProps

export { ReferAFriend }
